<!-- ライブ視聴、スケジュール、アーカイブ共通レイアウトページ -->
<script setup lang="ts">
  import type { ComponentPublicInstance } from "vue";
  import { useDisplay } from "vuetify";
  import { LiveInfo } from "~/entities/LiveInfo";
  import { useLoginStore } from "~/stores/LoginStore";
  import { useAgoraStore } from "~/stores/AgoraStore";
  import { useHistoryStore } from "~/stores/HistoryStore";
  const historyStore = useHistoryStore();

  interface Props {
    liveInfo?: LiveInfo;
    loading?: boolean;
    type: "live" | "schedule" | "archive" | "live_host";
  }
  const props = defineProps<Props>();

  const loginStore = useLoginStore();
  const agoraStore = useAgoraStore();

  // typeがスケジュールかどうか
  const isSchedule = computed<boolean>(() => props.type === "schedule");

  // typeがアーカイブかどうか
  const isArchive = computed<boolean>(() => props.type === "archive");

  // typeがライブ視聴かどうか
  const isLive = computed<boolean>(() => props.type === "live");

  // typeがライブ配信かどうか
  const isLiveHost = computed<boolean>(() => props.type === "live_host");

  // 自身がこのコンテンツのオーナー(講師)かどうか
  const isOwner = computed<boolean>(() => props.liveInfo?.isOwner ?? false);

  // チャットをリードオンリーにするかどうか
  const readonlyChat = computed<boolean>(() => {
    if (!isLive.value && !isLiveHost.value) {
      return true;
    }
    if (!showContents.value) {
      return true;
    }
    // liveEndDateTimeに値が設定されている場合は配信が終了しているのでreadonlyにする
    if (props.liveInfo?.liveEndDateTime) {
      return true;
    }

    return false;
  });

  // スケジュール用開始テキスト
  const startText = computed<string>(() => {
    if (props.liveInfo?.liveScheduleDateTime) {
      return FormatDateString(props.liveInfo.liveScheduleDateTime, "MM月dd日(E)HH:mm頃にライブ配信が始まります");
    }
    return "";
  });

  // レスポンシブ対応
  const { height } = useDisplay();
  const isSp = isSpDisplayRef();
  const isTablet = isTabletAndSpWithoutLandscapeDisplayRef();
  const isSpLandscape = isSpLandscapeRef();

  const isTabletOrSpLandscape = computed(() => {
    return isTablet.value || isSpLandscape.value;
  });

  // チャットをサイドに表示するかどうか
  const displayChatOnSide = computed<boolean>(() => {
    return !isTablet.value;
  });

  // SP版チャットを表示するかどうか
  const showMobileChatToggle = ref<boolean>(false);
  const showMobileChat = computed<boolean>(() => {
    // SPの場合のみフラグを返す
    if (!displayChatOnSide.value) {
      return showMobileChatToggle.value;
    }
    // それ以外はfalse固定
    return false;
  });

  const canShowDialog = ref(false);

  // SP版ではページ表示時にチャットエリアをアニメーションさせる
  onMounted(() => {
    // スケジュールではチャットは開かない
    if (!isSchedule.value && !isLiveHost.value) {
      setTimeout(() => {
        onClickChat();
      }, 300);
    }
    // レイアウト確定の1秒後にダイアログを表示する
    setTimeout(() => {
      canShowDialog.value = true;
    }, 1000);
  });
  // チャットを開くボタンクリックイベント
  const onClickChat = () => {
    if (!displayChatOnSide.value) {
      if (!props.liveInfo?.id) {
        // 今すぐ配信の場合はチャットを開けないようにする
        return;
      }
      showMobileChatToggle.value = !showMobileChatToggle.value;
      // チャットエリアはheight指定しないと、縦に伸びてしまうので少し時間を置いてからheightを調整する
      // TODO: なんとかしたい
      setTimeout(() => {
        setMobileChatHeight();
      }, 100);
    }
  };
  // チャットの閉じるボタンクリックイベント
  const onClickCloseChat = () => {
    if (!displayChatOnSide.value) {
      showMobileChatToggle.value = !showMobileChatToggle.value;
    }
  };

  // 配信するボタンクリックイベント
  const onClickStreamingStart = () => {
    // 配信画面へ
    historyStore.to(`/plan/${props.liveInfo?.plan?.id}/live/${props.liveInfo?.id}/host`);
  };

  // 編集するボタンクリックイベント
  const onClickEdit = () => {
    historyStore.to(`/plan/${props.liveInfo?.plan?.id}/distribute/live/${props.liveInfo.id}/edit`);
  };

  // モバイルチャットエリアの高さ
  const mobileChatMinHeight = 500;
  const mobileChatHeight = ref<number>(mobileChatMinHeight);
  const liveArea = ref<ComponentPublicInstance | null>(null);
  const setMobileChatHeight = () => {
    if (liveArea.value) {
      // 画面縦幅からヘッダーとライブエリアを除いた高さをチャットエリアの高さとする
      if (isTablet.value) {
        mobileChatHeight.value = height.value - 100 - liveArea.value.$el.clientHeight;
      } else {
        // PC版は最低の高さを設定
        mobileChatHeight.value = Math.max(mobileChatMinHeight, height.value - 100 - liveArea.value.$el.clientHeight);
      }
    } else {
      mobileChatHeight.value = mobileChatMinHeight;
    }
  };

  // コンテンツを見ることができるかどうか
  const showContents = computed<boolean>(() => {
    return isShowableContent(props.liveInfo);
  });

  // 仮解約中かどうか
  const isProvisionalCancellation = computed<boolean>(() => isProvisionalCancellationContent(props.liveInfo));

  const loaded = computed<boolean>(() => !!props.liveInfo && !!loginStore.loginInfo);

  const liveStartDateTime = computed<string>(() => {
    if (!props.liveInfo) {
      return "";
    }
    if (isLive.value || isLiveHost.value) {
      if (!props.liveInfo.liveEndDateTime) {
        return props.liveInfo.liveStartDateTime;
      }
    }
    return "";
  });

  const isAudioMuted = computed<boolean>(() => {
    if (isLive.value) {
      if (!props.liveInfo?.liveEndDateTime) {
        return agoraStore.isAudioMuted;
      }
    }
    return false;
  });

  const showCalendarButton = computed<boolean>(() => {
    return isSchedule.value;
  });

  const onClickToPayment = () => {
    return historyStore.to({
      path: `/plan/${props.liveInfo?.plan?.id}/payment`,
    });
  };

  const url = computed<string | null>(() => {
    if (!props.liveInfo?.id) {
      return null;
    }
    if (isArchive.value) {
      return `/plan/${props.liveInfo?.plan?.id}/livearchive/${props.liveInfo?.id}`;
    } else if (isSchedule.value) {
      return `/plan/${props.liveInfo?.plan?.id}/liveschedule/${props.liveInfo?.id}`;
    }
    return `/plan/${props.liveInfo?.plan?.id}/live/${props.liveInfo?.id}`;
  });

  const aspectRatio = computed<number>(() => {
    // 未入会時の講座限定コンテンツ閲覧モーダル用にアスペクト比を調整
    // https://invinc.slack.com/archives/C04HG0404HJ/p1706853467306339?thread_ts=1706668549.072769&cid=C04HG0404HJ
    if (!showContents.value && !isProvisionalCancellation.value && !loginStore.isLogin() && isSp.value) {
      return 1;
    }
    return 16 / 9;
  });

  const postDateTime = computed<string>(() => {
    if (props.liveInfo?.liveStartDateTime) {
      return props.liveInfo?.liveStartDateTime;
    }
    if (props.liveInfo?.liveScheduleDateTime) {
      return props.liveInfo?.liveScheduleDateTime;
    }
    return "";
  });

  const settingPath = computed<string>(() => {
    if (props.liveInfo?.isOwner) {
      if (isArchive.value) {
        return `/plan/${props.liveInfo?.plan?.id}/distribute/livearchive/${props.liveInfo?.id}/edit`;
      } else if (isSchedule.value) {
        return `/plan/${props.liveInfo?.plan?.id}/distribute/live/${props.liveInfo?.id}/edit`;
      }
    }
    return "";
  });
</script>

<template>
  <OrganismsLiveWithChatLayout
    v-if="loaded"
    ref="liveArea"
    :aspect-ratio="aspectRatio"
    :display-chat-on-side="displayChatOnSide"
    :show-chat-close-button="liveInfo?.id ? true : false"
  >
    <template #video>
      <template v-if="showContents">
        <slot name="video"></slot>
      </template>
      <template v-else>
        <v-img :src="liveInfo?.thumbnailImage" contain aspect-ratio="16/9" />
        <MoleculesContentsDialog
          v-if="canShowDialog && isProvisionalCancellation"
          message="講座仮解約中です。支払い情報を更新してください。"
          button-title="支払い情報を更新する"
          @click="onClickToPayment"
        />
        <MoleculesContentsNoPermissionDialog
          v-else-if="canShowDialog"
          :plan-id="liveInfo?.plan?.id"
          :plan-title="liveInfo?.plan?.title"
          :content-type="isArchive ? `動画` : `ライブ`"
        />
      </template>
    </template>
    <template v-if="displayChatOnSide" #chat="chatProps">
      <OrganismsLiveChat
        v-if="liveInfo?.id"
        :live-id="liveInfo.id"
        :readonly="readonlyChat"
        :is-archive="isArchive"
        :height="chatProps.height"
        :is-owner="isOwner"
        :owner-id="liveInfo?.planOwner?.userId"
      />
    </template>
    <!-- 動画の上に表示するもの -->
    <template #topinfo>
      <template v-if="isSchedule">
        <v-sheet class="d-flex align-center justify-center text-start w-100" style="position: relative" color="main-01">
          <div>{{ startText }}</div>
        </v-sheet>
      </template>
      <template v-if="!isTabletOrSpLandscape && (isLiveHost || isLive)">
        <div class="d-flex align-end justify-end flex-wrap w-100 mb-1">
          <div v-if="isAudioMuted">
            <AtomsSvgIcon :width="24" img-path="assets/images/icon/video-mute-wigh-bg.svg" />
          </div>
          <div v-if="showContents && liveStartDateTime" class="mx-2">
            <AtomsLiveTimer :start-date-time="liveStartDateTime" />
          </div>
        </div>
      </template>
    </template>
    <!-- 動画の下に表示するもの -->
    <template #bottominfo>
      <template v-if="isTabletOrSpLandscape && (isLiveHost || isLive)">
        <div class="d-flex align-end justify-end flex-wrap w-100 my-2">
          <div v-if="isAudioMuted">
            <AtomsSvgIcon :width="24" img-path="assets/images/icon/video-mute-wigh-bg.svg" />
          </div>
          <div v-if="liveStartDateTime" class="mx-2">
            <AtomsLiveTimer :start-date-time="liveStartDateTime" />
          </div>
        </div>
      </template>
    </template>
  </OrganismsLiveWithChatLayout>

  <template v-if="!displayChatOnSide">
    <Transition name="rollup">
      <v-sheet v-show="showMobileChat" class="w-100" :height="mobileChatHeight">
        <OrganismsLiveChat
          v-if="liveInfo?.id"
          :live-id="liveInfo.id"
          :readonly="readonlyChat"
          :is-archive="isArchive"
          :height="mobileChatHeight"
          :is-owner="isOwner"
          :owner-id="liveInfo?.planOwner?.userId"
          :show-back-button="true"
          @click-close="onClickCloseChat"
        />
      </v-sheet>
    </Transition>
  </template>
  <div v-if="liveInfo" v-show="!showMobileChat">
    <!-- googleカレンダー -->
    <AtomsContentInformationCard v-if="showCalendarButton">
      <template #header> Googleカレンダーに予定を追加してライブを見る </template>
      <AtomsGoogleCalendarButton :live-info="liveInfo" />
    </AtomsContentInformationCard>

    <!-- コンテンツ情報 -->
    <MoleculesContentInformation
      v-if="liveInfo"
      :url="url"
      :tags="liveInfo?.tags"
      :title="liveInfo?.contentsTitle"
      :description="liveInfo?.description"
      :plan-id="liveInfo?.plan?.id"
      :plan-title="liveInfo?.plan?.title"
      :plan-icon="liveInfo?.plan?.thumbnailImage"
      :show-chat-button="isTablet"
      :show-device-select="isLiveHost"
      :loading="loading"
      :is-free="liveInfo?.isFree"
      :content-id="liveInfo?.id.toString()"
      :is-good="liveInfo?.isGood"
      :good-count="liveInfo?.goodCount"
      :publish-date-time="postDateTime"
      :setting-path="settingPath"
      :downloadable="false"
      :is-bookmark="liveInfo?.isBookmark"
    >
      <template #right-title>
        <!-- 配信する、編集するボタン -->
        <v-sheet v-if="isSchedule && isOwner" color="transparent">
          <v-btn
            block
            class="rounded px-2 mb-2 owner-button-text"
            color="main-01"
            :height="40"
            @click="onClickStreamingStart"
            >配信する</v-btn
          >
          <v-btn
            block
            class="rounded px-2 mb-2 owner-button-text"
            color="monochrome-03"
            :height="40"
            @click="onClickEdit"
            >編集する</v-btn
          >
        </v-sheet>
      </template>
      <template #under-tab>
        <v-sheet v-if="!displayChatOnSide" class="mt-4 mb-5 d-flex justify-center" color="transparent">
          <AtomsButton
            rounded="xl"
            color="monochrome-06"
            :height="40"
            :disabled="!props.liveInfo?.id"
            @click="onClickChat"
            >チャットを開く</AtomsButton
          >
        </v-sheet>
        <v-sheet v-if="isLiveHost" class="mt-4 mb-5 d-flex flex-column flex-sm-row" color="transparent">
          <OrganismsAgoraCameraSelect />
          <OrganismsAgoraMicrophoneSelect class="ml-0 ml-sm-2" />
        </v-sheet>
      </template>
    </MoleculesContentInformation>
    <div v-if="!isLiveHost">
      <OrganismsContentsPageStreamingList
        v-if="liveInfo"
        :plan-id="liveInfo?.plan?.id || ''"
        :contents-id="liveInfo?.id"
      />
    </div>
    <slot name="bottom"></slot>
  </div>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .text-start {
    height: 38px;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.8em;

    @include display_sp {
      height: 20px;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.8em;
    }
  }

  .owner-button-text {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.8em;
  }

  .rollup-enter-active,
  .rollup-leave-active {
    transition:
      transform 0.3s,
      opacity 0.3s ease;
  }

  .rollup-enter-from,
  .rollup-leave-to {
    transform: translateY(400px);
    opacity: 0;
  }
</style>
